import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EncryptionManagementPanel = ({ onRefresh }) => {
  const [encryptionConfig] = useState({
    algorithm: 'AES-256',
    keyRotationDays: 90,
    lastRotation: '2026-01-15',
    nextRotation: '2026-04-15',
    transitEncryption: true,
    atRestEncryption: true
  });

  return (
    <div className="space-y-6">
      {/* Encryption Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Encryption at Rest</p>
              <div className="flex items-center gap-2">
                <Icon name="Lock" size={20} className="text-green-600" />
                <span className="text-lg font-bold text-green-900 dark:text-green-100">Active</span>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">AES-256 encryption enabled</p>
            </div>
            <Icon name="CheckCircle" size={24} className="text-green-600" />
          </div>
        </div>

        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2">Encryption in Transit</p>
              <div className="flex items-center gap-2">
                <Icon name="Shield" size={20} className="text-green-600" />
                <span className="text-lg font-bold text-green-900 dark:text-green-100">Active</span>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">TLS 1.3 enabled</p>
            </div>
            <Icon name="CheckCircle" size={24} className="text-green-600" />
          </div>
        </div>
      </div>

      {/* Encryption Configuration */}
      <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Icon name="Settings" size={20} />
          Encryption Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Encryption Algorithm
            </label>
            <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600">
              <Icon name="Lock" size={18} className="text-blue-600" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">{encryptionConfig?.algorithm}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Key Rotation Period
            </label>
            <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600">
              <Icon name="RefreshCw" size={18} className="text-blue-600" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">{encryptionConfig?.keyRotationDays} days</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Last Key Rotation
            </label>
            <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600">
              <Icon name="Calendar" size={18} className="text-blue-600" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">{encryptionConfig?.lastRotation}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Next Key Rotation
            </label>
            <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600">
              <Icon name="Calendar" size={18} className="text-amber-600" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">{encryptionConfig?.nextRotation}</span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <Button variant="default" size="sm" iconName="RefreshCw">
            Rotate Keys Now
          </Button>
          <Button variant="outline" size="sm" iconName="Download">
            Export Certificate
          </Button>
        </div>
      </div>

      {/* PNG Government Security Standards */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={24} className="text-blue-600 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
              PNG Government Security Standards Compliance
            </h4>
            <ul className="space-y-1 text-sm text-blue-700 dark:text-blue-300">
              <li className="flex items-center gap-2">
                <Icon name="CheckCircle" size={14} className="text-green-600" />
                AES-256 encryption meets PNG data protection requirements
              </li>
              <li className="flex items-center gap-2">
                <Icon name="CheckCircle" size={14} className="text-green-600" />
                TLS 1.3 for secure data transmission
              </li>
              <li className="flex items-center gap-2">
                <Icon name="CheckCircle" size={14} className="text-green-600" />
                90-day key rotation policy active
              </li>
              <li className="flex items-center gap-2">
                <Icon name="CheckCircle" size={14} className="text-green-600" />
                Compliant with PNG labor law data retention
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EncryptionManagementPanel;